import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, BarChart3, Calendar, Upload, Shield, Zap, Check, Tag, Brain, Sparkles, Target, MessageCircle, Award, LineChart, Users, Gauge, Star, ArrowRight, ChevronRight, Camera, Info, RefreshCw, GraduationCap, Lock, BookOpen, Flame } from 'lucide-react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

import API from '../utils/api';

// Animation variants for scroll reveal
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

// Animated section wrapper component
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { 
          opacity: 1, 
          y: 0,
          transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Counter animation component
const AnimatedCounter = ({ target, duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseInt(target);
      const incrementTime = (duration * 1000) / end;
      const timer = setInterval(() => {
        start += Math.ceil(end / 50);
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(start);
        }
      }, incrementTime);
      return () => clearInterval(timer);
    }
  }, [isInView, target, duration]);
  
  return <span ref={ref}>{count.toLocaleString()}</span>;
};

const LandingPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [billingInterval, setBillingInterval] = useState('month');

  // KB Launch Countdown (01.05.2026 00:00 UTC)
  const KB_LAUNCH_TS = Date.UTC(2026, 4, 1, 0, 0, 0);
  const calcCountdown = () => {
    const diff = KB_LAUNCH_TS - Date.now();
    if (diff <= 0) return null;
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff / 3600000) % 24),
      minutes: Math.floor((diff / 60000) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };
  const [countdown, setCountdown] = useState(calcCountdown());
  const [publicStats, setPublicStats] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setCountdown(calcCountdown()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/public/stats`);
        if (r.ok) setPublicStats(await r.json());
      } catch (e) {}
    })();
  }, []);

  // Track scroll position for navbar transparency
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch pricing from API
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API}/pricing`);
        if (response.ok) {
          const data = await response.json();
          setPricing(data);
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    };
    fetchPricing();
  }, []);

  // Calculate display price based on billing interval
  const getDisplayPrice = (planId) => {
    const defaults = { basic: 8.99, pro: 14.99, premium: 25.99 };
    if (!pricing || !pricing[planId]) {
      const base = defaults[planId];
      if (billingInterval === 'lifetime') {
        return planId === 'premium'
          ? { price: 499.99, isLifetime: true, hasDiscount: false }
          : { price: base, originalPrice: null, hasDiscount: false, notAvailable: true };
      }
      if (billingInterval === 'year') {
        const yearlyTotal = base * 12;
        return { price: base, yearlyTotal, monthlyEquivalent: base, originalPrice: null, hasDiscount: false, savings: 0 };
      }
      return { price: base, originalPrice: null, hasDiscount: false };
    }
    const plan = pricing[planId];
    const hasMonthlyDiscount = plan.discount_active && plan.discount_percent > 0;
    const monthlyPrice = hasMonthlyDiscount
      ? parseFloat((plan.price * (1 - plan.discount_percent / 100)).toFixed(2))
      : plan.price;

    if (billingInterval === 'lifetime') {
      if (planId === 'premium') {
        const lifetimePrice = plan.lifetime_price || 499.99;
        return { price: lifetimePrice, isLifetime: true, hasDiscount: false };
      }
      return { price: monthlyPrice, originalPrice: null, hasDiscount: false, notAvailable: true };
    }

    if (billingInterval === 'year') {
      const yearlyDiscount = plan.yearly_discount_percent || 0;
      const fullYearlyPrice = monthlyPrice * 12;
      const yearlyTotal = parseFloat((fullYearlyPrice * (1 - yearlyDiscount / 100)).toFixed(2));
      const monthlyEquivalent = parseFloat((yearlyTotal / 12).toFixed(2));
      const savings = parseFloat((fullYearlyPrice - yearlyTotal).toFixed(2));
      return {
        price: monthlyEquivalent,
        yearlyTotal,
        monthlyEquivalent,
        originalPrice: yearlyDiscount > 0 ? monthlyPrice : null,
        hasDiscount: yearlyDiscount > 0,
        discountPercent: yearlyDiscount,
        savings,
      };
    }
    return {
      price: monthlyPrice,
      originalPrice: hasMonthlyDiscount ? plan.price : null,
      hasDiscount: hasMonthlyDiscount,
      discountPercent: plan.discount_percent,
    };
  };

  const formatPrice = (price) => {
    return price.toFixed(2).replace('.', ',');
  };

  // Smooth scroll to section with offset for fixed navbar
  const scrollToSection = useCallback((e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - navbarHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  const features = [
    {
      icon: <Brain className="h-8 w-8 text-purple-400" />,
      title: 'KI Trading Mentor',
      description: 'Dein persönlicher KI-Coach analysiert deine Trades und gibt dir konkrete Verbesserungsvorschläge.',
      highlight: true
    },
    {
      icon: <Award className="h-8 w-8 text-amber-400" />,
      title: 'Skill-Score System',
      description: '4-Dimensionen Bewertung: Risiko-Management, Konsistenz, Disziplin & Profitabilität mit Rang-System.',
      highlight: true
    },
    {
      icon: <Camera className="h-8 w-8 text-emerald-400" />,
      title: 'KI Screenshot-Import',
      description: 'Lade einen Screenshot hoch – unsere KI erkennt automatisch Symbol, Einstieg, Ausstieg und P&L.',
      highlight: true
    },
    {
      icon: <Calendar className="h-8 w-8 text-primary" />,
      title: 'Trading-Kalender',
      description: 'Visualisiere deine Performance mit Glow-Effekten, Weekly-Zusammenfassung und Check-in Tracking.'
    },
    {
      icon: <RefreshCw className="h-8 w-8 text-emerald-400" />,
      title: 'Multi-Währung',
      description: 'Automatische Währungsumrechnung zwischen USD, EUR, GBP und CHF. Alle Werte in deiner Wunschwährung.'
    },
    {
      icon: <Target className="h-8 w-8 text-primary" />,
      title: 'Strategie-Management',
      description: 'Erstelle und verwalte Trading-Strategien. Analysiere Performance pro Strategie separat.'
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-primary" />,
      title: 'Pro Statistiken',
      description: 'TWR%, Win Rate, Profit Factor, Drawdown und 10+ weitere Metriken auf einen Blick.'
    },
  ];

  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      features: ['1 Trading-Konto', '1 Trading-Strategie', 'CSV-Import', 'Dashboard-Statistiken', 'Trading-Kalender'],
      highlighted: false,
    },
    {
      id: 'pro',
      name: 'Pro',
      features: ['5 Trading-Konten', '5 Trading-Strategien', 'Alles aus Basic', 'Screenshot-Upload mit KI', 'Notizen & Emotionen', 'PDF-Export'],
      highlighted: true,
    },
    {
      id: 'premium',
      name: 'Premium',
      features: ['Unbegrenzte Konten', 'Unbegrenzte Strategien', 'Alles aus Pro', 'KI Trading-Mentor', 'Skill-Score System', 'Premium-Support'],
      highlighted: false,
    },
  ];

  // Skill score ranks for display
  const skillRanks = [
    { name: 'Einsteiger', range: '<1000', color: 'text-zinc-400' },
    { name: 'Stabil', range: '1000-1200', color: 'text-emerald-400' },
    { name: 'Fortgeschritten', range: '1200-1400', color: 'text-emerald-400' },
    { name: 'Professionell', range: '1400-1600', color: 'text-blue-400' },
    { name: 'Experte', range: '1600-1800', color: 'text-purple-400' },
    { name: 'Elite', range: '1800+', color: 'text-amber-400' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation - Transparent at top, solid on scroll */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/95 backdrop-blur-lg border-b border-white/[0.06] shadow-lg shadow-black/20' 
          : 'bg-transparent border-b border-transparent'
      }`}>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <TrendingUp className="h-8 w-8 text-primary" />
            <span className="text-xl font-heading font-semibold">TradeJourn<span className="text-primary">.ai</span></span>
          </Link>
          
          {/* Center Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            <a 
              href="#features" 
              className="text-sm text-zinc-400 hover:text-white transition-colors"
            >
              Features
            </a>
            <a 
              href="#ai-mentor" 
              className="text-sm text-zinc-400 hover:text-white transition-colors"
            >
              KI Mentor
            </a>
            <a 
              href="#pricing" 
              className="text-sm text-zinc-400 hover:text-white transition-colors"
            >
              Pricing
            </a>
            <a 
              href="#faq" 
              className="text-sm text-zinc-400 hover:text-white transition-colors"
            >
              FAQ
            </a>
          </div>
          
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button data-testid="go-to-dashboard-btn">{t('dashboard')}</Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className={isScrolled ? '' : 'hover:bg-white/[0.08]'} data-testid="landing-login-btn">{t('login')}</Button>
                </Link>
                <Link to="/register">
                  <Button data-testid="landing-register-btn">{t('getStarted')}</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section - Focus on AI Mentor */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden">
        {/* P2: Einheitlicher subtiler Hintergrund */}
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/20 via-transparent to-transparent" />
        <motion.div 
          className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/5 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl"
          animate={{ 
            scale: [1.1, 1, 1.1],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* P2: Cleaner USP Badge - weniger Farben */}
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/[0.08] mb-8"
            >
              <Brain className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium text-zinc-300">Deutschlands erstes KI-gesteuertes Trading Journal</span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-display mb-6 tracking-tight"
            >
              Dein persönlicher
              <motion.span 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="block text-primary mt-2"
              >
                KI Trading Mentor
              </motion.span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto"
            >
              Mehr als ein Journal: Eine KI analysiert deine Trades, bewertet deine Skills und gibt dir personalisierte Empfehlungen für besseres Trading.
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/register">
                <Button size="lg" className="text-lg px-8 glow-primary group" data-testid="hero-cta-btn">
                  <Brain className="h-5 w-5 mr-2 group-hover:animate-pulse" />
                  Jetzt starten
                  <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="#ai-mentor">
                <Button size="lg" variant="outline" className="text-lg px-8 border-emerald-500/50 hover:bg-emerald-500/10">
                  KI Mentor entdecken
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI Mentor Main Feature Section - THE USP */}
      <section id="ai-mentor" className="py-24 relative overflow-hidden">
        {/* P2: Einheitlicher Hintergrund - nur subtile Akzente für KI Bereich */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-950/10 to-transparent" />
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/[0.08] mb-6">
              <Award className="h-4 w-4 text-amber-400" />
              <span className="text-sm font-medium text-zinc-300">Unser Alleinstellungsmerkmal</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display mb-6">
              Trading-Coaching der
              <span className="text-primary"> Zukunft</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Unser KI Mentor analysiert nicht nur deine Performance – er bewertet deine Trading-Skills 
              mit einem einzigartigen Punkte-System und zeigt dir genau, wo du dich verbessern kannst.
            </p>
          </motion.div>

          {/* Main Feature Grid */}
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            
            {/* Left Column - Skill Score System - BUNTE FARBEN BEIBEHALTEN */}
            <motion.div 
              className="space-y-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={fadeInLeft}
            >
              {/* Skill Score Card - Bunte Score-Visualisierung bleibt! */}
              <div className="relative rounded-2xl border border-white/[0.1] bg-white/[0.02] backdrop-blur-sm p-8 shadow-2xl">
                <div className="absolute -top-3 left-6 px-4 py-1 bg-primary rounded-full">
                  <span className="text-xs font-bold text-white">NEU: Skill-Score System</span>
                </div>
                
                {/* Info Icon with Tooltip */}
                <div className="absolute top-4 right-4 group">
                  <button className="p-2 rounded-full bg-white/[0.04] hover:bg-white/[0.08] transition-colors border border-white/[0.08] hover:border-primary/30">
                    <Info className="h-4 w-4 text-zinc-400 group-hover:text-primary transition-colors" />
                  </button>
                  {/* Tooltip */}
                  <div className="absolute right-0 top-full mt-2 w-72 p-4 bg-zinc-900/95 backdrop-blur-md border border-white/[0.1] rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                      <Gauge className="h-4 w-4 text-emerald-400" />
                      So wird dein Score berechnet
                    </h4>
                    <div className="space-y-2 text-xs text-zinc-300">
                      <p><span className="text-emerald-400 font-semibold">Risiko-Management:</span> Stop-Loss Einhaltung, Positionsgrößen, Risk/Reward Ratio</p>
                      <p><span className="text-purple-400 font-semibold">Konsistenz:</span> Regelmäßigkeit, Systemtreue, Strategie-Einhaltung</p>
                      <p><span className="text-emerald-400 font-semibold">Prozess-Disziplin:</span> Journal-Führung, Emotionskontrolle, Regelbeachtung</p>
                      <p><span className="text-amber-400 font-semibold">Profitabilität:</span> Gewinn/Verlust Verhältnis, Wachstum, Drawdown</p>
                    </div>
                    <div className="mt-3 pt-3 border-t border-zinc-700">
                      <p className="text-[10px] text-zinc-500">Maximal 2.000 Punkte pro Dimension. Der Gesamtscore ist der Durchschnitt aller Dimensionen.</p>
                    </div>
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold mb-6 mt-2">4 Dimensionen deines Tradings</h3>
                
                {/* Skill Bars */}
                <div className="space-y-5">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="flex items-center gap-2">
                        <Gauge className="h-4 w-4 text-emerald-400" />
                        Risiko-Management
                      </span>
                      <span className="text-emerald-400 font-mono">1.340</span>
                    </div>
                    <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full" 
                        initial={{ width: 0 }}
                        whileInView={{ width: '67%' }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-purple-400" />
                        Konsistenz & Systemtreue
                      </span>
                      <span className="text-purple-400 font-mono">1.180</span>
                    </div>
                    <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full" 
                        initial={{ width: 0 }}
                        whileInView={{ width: '59%' }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="flex items-center gap-2">
                        <Brain className="h-4 w-4 text-emerald-400" />
                        Prozess-Disziplin
                      </span>
                      <span className="text-emerald-400 font-mono">1.520</span>
                    </div>
                    <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full" 
                        initial={{ width: 0 }}
                        whileInView={{ width: '76%' }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="flex items-center gap-2">
                        <LineChart className="h-4 w-4 text-amber-400" />
                        Profitabilität
                      </span>
                      <span className="text-amber-400 font-mono">1.450</span>
                    </div>
                    <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full" 
                        initial={{ width: 0 }}
                        whileInView={{ width: '72%' }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Overall Score */}
                <div className="mt-8 pt-6 border-t border-zinc-800 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Gesamtbewertung</p>
                    <p className="text-3xl font-mono font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-purple-400">
                      1.370
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground mb-1">Rang</p>
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30">
                      <Star className="h-4 w-4 text-emerald-400" />
                      <span className="font-semibold text-emerald-400">Fortgeschritten</span>
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Rank Ladder */}
              <motion.div 
                className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeInUp}
              >
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Award className="h-5 w-5 text-amber-400" />
                  Rang-System
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {skillRanks.map((rank, i) => (
                    <motion.div 
                      key={i} 
                      className="text-center p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50"
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                    >
                      <p className={`font-semibold ${rank.color}`}>{rank.name}</p>
                      <p className="text-xs text-muted-foreground">{rank.range} Punkte</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
            
            {/* Right Column - AI Analysis & Features */}
            <motion.div 
              className="space-y-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={fadeInRight}
            >
              {/* AI Analysis Card */}
              <div className="relative rounded-2xl border border-purple-500/30 bg-zinc-900/90 backdrop-blur-sm p-8 shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500/30 to-purple-500/30 flex items-center justify-center border border-purple-500/30">
                    <Brain className="h-7 w-7 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold">KI Mentor Analyse</h4>
                    <p className="text-sm text-muted-foreground">Personalisiertes Feedback</p>
                  </div>
                </div>
                
                {/* Mock Analysis */}
                <motion.div 
                  className="space-y-4"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={staggerContainer}
                >
                  <motion.div 
                    className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4"
                    variants={fadeInUp}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="text-sm font-medium text-emerald-400">Stärke erkannt</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Dein Risiko-Management hat sich im letzten Monat um <span className="text-emerald-400 font-semibold">+45 Punkte</span> verbessert. Du hältst dich konsequenter an deine Stop-Loss-Regeln.
                    </p>
                  </motion.div>
                  
                  <motion.div 
                    className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4"
                    variants={fadeInUp}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-amber-400" />
                      <span className="text-sm font-medium text-amber-400">Verbesserungspotenzial</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Deine Konsistenz schwankt an Freitagen. <span className="text-amber-400 font-semibold">Empfehlung:</span> Reduziere die Positionsgröße vor dem Wochenende.
                    </p>
                  </motion.div>
                  
                  <motion.div 
                    className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4"
                    variants={fadeInUp}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="text-sm font-medium text-emerald-400">Nächstes Ziel</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Noch <span className="text-emerald-400 font-semibold">60 Punkte</span> bis zum Rang "Professionell". Fokussiere dich auf deine Systemtreue.
                    </p>
                  </motion.div>
                </motion.div>
              </div>
              
              {/* Feature List */}
              <motion.div 
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
              >
                <motion.div 
                  className="flex items-start gap-4 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-emerald-500/30 transition-colors"
                  variants={fadeInUp}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Users className="h-5 w-5 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Trader-Profil</h4>
                    <p className="text-xs text-muted-foreground">KI lernt deinen Trading-Stil</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex items-start gap-4 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-purple-500/30 transition-colors"
                  variants={fadeInUp}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <LineChart className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Score-Verlauf</h4>
                    <p className="text-xs text-muted-foreground">Verfolge deinen Fortschritt</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex items-start gap-4 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-emerald-500/30 transition-colors"
                  variants={fadeInUp}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Psychologie-Coaching</h4>
                    <p className="text-xs text-muted-foreground">Emotionen erkennen & meistern</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex items-start gap-4 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-amber-500/30 transition-colors"
                  variants={fadeInUp}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Target className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Konkrete Ziele</h4>
                    <p className="text-xs text-muted-foreground">Klarer Weg nach oben</p>
                  </div>
                </motion.div>
              </motion.div>
              
              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Link to="/register" className="block">
                  <Button size="lg" className="w-full text-lg glow-primary group" data-testid="ai-mentor-cta-btn">
                    <Brain className="h-5 w-5 mr-2" />
                    KI Mentor entdecken
                    <ChevronRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Dashboard Preview - After AI Mentor */}
      <AnimatedSection className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-display mb-4">
              Professionelles Trading Dashboard
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Alle wichtigen Metriken auf einen Blick: TWR, Win-Rate, Profit-Faktor, Trading-Kalender und mehr.
            </p>
          </motion.div>
          <motion.div 
            className="max-w-5xl mx-auto rounded-xl border border-zinc-800 overflow-hidden shadow-2xl"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={scaleIn}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_tradelog-97/artifacts/q68x8fdw_image.png"
              alt="TradeJourn.ai Dashboard"
              className="w-full"
            />
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Features */}
      <div id="features">
      <AnimatedSection className="py-20 bg-zinc-900/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/[0.08] mb-6">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-zinc-300">Unsere Features</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-display mb-4">
              {t('features')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Alles was du brauchst, um dein Trading auf das nächste Level zu bringen.
            </p>
          </motion.div>
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {features.map((feature, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className={`h-full transition-all duration-300 hover:-translate-y-1 ${
                  feature.highlight 
                    ? 'border-primary/30 hover:border-primary/50' 
                    : 'hover:border-white/[0.12]'
                }`}>
                  <CardHeader>
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-3 ${
                      feature.highlight 
                        ? 'bg-primary/10 border border-primary/20' 
                        : 'bg-white/[0.04]'
                    }`}>
                      {feature.icon}
                    </div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {feature.title}
                      {feature.highlight && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary text-white">
                          USP
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed">{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </AnimatedSection>
      </div>

      {/* Pricing */}
      <div id="pricing">
      <AnimatedSection className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-display mb-4">
              {t('choosePlan')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {billingInterval === 'month' ? t('monthly') : billingInterval === 'year' ? (language === 'de' ? 'Jährliche Abrechnung' : 'Billed annually') : (language === 'de' ? 'Einmalzahlung' : 'One-time payment')}
            </p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-full px-2 py-1.5" data-testid="billing-toggle-landing">
              <button
                onClick={() => setBillingInterval('month')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  billingInterval === 'month'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="billing-monthly-btn"
              >
                {language === 'de' ? 'Monatlich' : 'Monthly'}
              </button>
              <button
                onClick={() => setBillingInterval('year')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  billingInterval === 'year'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="billing-yearly-btn"
              >
                {language === 'de' ? 'Jährlich' : 'Yearly'}
                <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full font-bold">
                  {language === 'de' ? 'Spare mehr' : 'Save more'}
                </span>
              </button>
              <button
                onClick={() => setBillingInterval('lifetime')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  billingInterval === 'lifetime'
                    ? 'bg-amber-500 text-black shadow-sm'
                    : 'text-zinc-400 hover:text-amber-400'
                }`}
                data-testid="billing-lifetime-btn"
              >
                Lifetime
              </button>
            </div>
          </motion.div>
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {plans.map((plan, index) => {
              const priceInfo = getDisplayPrice(plan.id);
              const isLifetimePremium = billingInterval === 'lifetime' && plan.id === 'premium';
              const isLifetimeOther = billingInterval === 'lifetime' && plan.id !== 'premium';
              return (
                <motion.div 
                  key={plan.id} 
                  variants={fadeInUp}
                  className={plan.highlighted ? 'md:-mt-4 md:mb-4' : ''}
                >
                  <Card 
                    className={`relative h-full transition-all duration-300 ${
                      isLifetimePremium
                        ? 'glow-gold bg-zinc-900 scale-105'
                        : isLifetimeOther
                          ? 'bg-zinc-900/30 border-zinc-800 opacity-60'
                          : plan.highlighted
                            ? 'border-primary bg-zinc-900 scale-105'
                            : 'bg-zinc-900/50 border-zinc-800 hover:border-primary/50'
                    }`}
                  >
                    {plan.highlighted && !isLifetimePremium && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full">
                        Beliebt
                      </div>
                    )}
                    {isLifetimePremium && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-xs font-bold px-3 py-1 rounded-full" data-testid="lifetime-badge">
                        Lifetime Deal
                      </div>
                    )}
                    {plan.id === 'premium' && !isLifetimePremium && (
                      <div className="absolute -top-3 right-4 bg-gradient-to-r from-emerald-500 to-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                        <Brain className="h-3 w-3" />
                        KI Mentor
                      </div>
                    )}
                    {priceInfo.hasDiscount && !priceInfo.isLifetime && (
                      <div className="absolute -top-3 left-4 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        -{priceInfo.discountPercent}%
                      </div>
                    )}
                    <CardHeader className="text-center pb-2">
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                      <div className="mt-4">
                        {priceInfo.isLifetime ? (
                          <>
                            <span className="text-4xl font-heading font-bold text-amber-400">
                              {formatPrice(priceInfo.price)}€
                            </span>
                            <span className="text-muted-foreground ml-1">{language === 'de' ? 'einmalig' : 'one-time'}</span>
                            <p className="text-xs text-amber-400/80 mt-2 font-medium">{language === 'de' ? 'Für immer Premium' : 'Premium forever'}</p>
                          </>
                        ) : (
                          <>
                            {priceInfo.hasDiscount && priceInfo.originalPrice && (
                              <span className="text-lg text-muted-foreground line-through mr-2">
                                {formatPrice(priceInfo.originalPrice)}€
                              </span>
                            )}
                            <span className={`text-4xl font-heading font-bold ${priceInfo.hasDiscount ? 'text-emerald-400' : ''}`}>
                              {formatPrice(priceInfo.price)}€
                            </span>
                            <span className="text-muted-foreground">/Monat</span>
                          </>
                        )}
                      </div>
                      {billingInterval === 'year' && priceInfo.yearlyTotal && (
                        <div className="mt-2 space-y-1">
                          <p className="text-xs text-muted-foreground">
                            {formatPrice(priceInfo.yearlyTotal)}€ / {language === 'de' ? 'Jahr' : 'year'}
                          </p>
                          {priceInfo.savings > 0 && (
                            <p className="text-xs font-semibold text-emerald-400" data-testid={`savings-${plan.id}`}>
                              {language === 'de' ? 'Du sparst' : 'You save'} {formatPrice(priceInfo.savings)}€
                            </p>
                          )}
                        </div>
                      )}
                    </CardHeader>
                    <CardContent className="pt-6">
                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm">
                            <Check className={`h-4 w-4 flex-shrink-0 ${feature.includes('KI') ? 'text-purple-400' : 'text-primary'}`} />
                            <span className={feature.includes('KI') || feature.includes('Skill') ? 'font-medium text-purple-300' : ''}>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {isLifetimeOther ? (
                        <p className="text-xs text-center text-muted-foreground italic py-2" data-testid={`lifetime-hint-${plan.id}`}>
                          {language === 'de' ? 'Lifetime nur für Premium' : 'Lifetime only for Premium'}
                        </p>
                      ) : (
                        <>
                        <Link to="/register">
                          <Button 
                            className={`w-full ${
                              isLifetimePremium
                                ? 'bg-gradient-to-r from-amber-500 to-yellow-400 text-black hover:from-amber-600 hover:to-yellow-500 font-bold'
                                : plan.highlighted
                                  ? 'glow-primary'
                                  : plan.id === 'premium'
                                    ? 'bg-gradient-to-r from-emerald-500 to-purple-500 hover:from-emerald-600 hover:to-purple-600'
                                    : ''
                            }`}
                            variant={plan.highlighted || plan.id === 'premium' ? 'default' : 'outline'}
                            data-testid={`plan-${plan.id}-btn`}
                          >
                            {isLifetimePremium ? (language === 'de' ? 'Lifetime sichern' : 'Get Lifetime') : plan.id === 'premium' ? 'Premium starten' : t('selectPlan')}
                          </Button>
                        </Link>
                        {isLifetimePremium && (
                          <p className="text-[10px] text-center text-zinc-500 mt-2">{language === 'de' ? 'Auch mit PayPal Ratenzahlung möglich' : 'PayPal Pay Later available'}</p>
                        )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </AnimatedSection>
      </div>

      {/* ==================== KB TEASER — COMING 01.05.2026 ==================== */}
      <AnimatedSection className="py-20 relative overflow-hidden" data-testid="landing-kb-teaser">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-emerald-500/10 blur-[120px]" />
          <div className="absolute bottom-0 -right-32 w-96 h-96 rounded-full bg-emerald-500/5 blur-[120px]" />
        </div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeInUp}
            className="max-w-6xl mx-auto"
          >
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              {/* Left: Copy */}
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-5">
                  <Flame className="h-3.5 w-3.5 text-emerald-400" />
                  <span className="text-[11px] font-semibold text-emerald-300 uppercase tracking-wider">
                    Coming 01.05.2026
                  </span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white leading-[1.1] mb-5">
                  Deine eigene{' '}
                  <span className="bg-gradient-to-r from-emerald-400 to-emerald-200 bg-clip-text text-transparent">
                    KI-Trading-Akademie
                  </span>
                </h2>
                <p className="text-base sm:text-lg text-zinc-400 leading-relaxed mb-6 max-w-xl">
                  Über 100 Lehrmodule zu Smart Money, ICT-Konzepten, Risikomanagement und Trading-Psychologie —
                  kombiniert mit einem persönlichen KI-Mentor, der jede deiner Fragen live beantwortet
                  und Textpassagen auf Knopfdruck erklärt.
                </p>

                {/* Live Countdown */}
                {countdown && (
                  <div className="mb-6" data-testid="landing-kb-countdown">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 font-semibold">
                      Launch in
                    </p>
                    <div className="grid grid-cols-4 gap-2 max-w-md">
                      {[
                        { v: countdown.days, l: 'Tage' },
                        { v: countdown.hours, l: 'Std' },
                        { v: countdown.minutes, l: 'Min' },
                        { v: countdown.seconds, l: 'Sek' },
                      ].map((b, i) => (
                        <div key={i} className="flex flex-col items-center">
                          <div className="w-full py-3 rounded-xl bg-gradient-to-br from-emerald-500/15 to-emerald-700/5 border border-emerald-500/30 text-center shadow-[0_0_20px_rgba(16,185,129,0.1)]">
                            <span className="font-heading text-2xl sm:text-3xl font-bold text-emerald-300 tabular-nums">
                              {String(b.v).padStart(2, '0')}
                            </span>
                          </div>
                          <span className="text-[9px] uppercase tracking-wider text-zinc-500 mt-1.5">{b.l}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2.5 mb-6">
                  {[
                    { icon: Brain, label: 'KI-Mentor für jede Frage (GPT-4o + Live-Web)' },
                    { icon: Target, label: 'Verständnis-Quiz nach jedem Thema' },
                    { icon: GraduationCap, label: 'Lernfortschritt, Streaks & Gelesen-Tracking' },
                    { icon: BookOpen, label: 'Über 100 kuratierte Themen mit Quellenangaben' },
                  ].map(({ icon: Icon, label }, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center shrink-0">
                        <Icon className="h-4 w-4 text-emerald-400" />
                      </div>
                      <span className="text-sm text-zinc-300">{label}</span>
                    </div>
                  ))}
                </div>

                {/* Social Proof Badge */}
                {publicStats?.registered_traders && (
                  <div
                    className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-full bg-zinc-900/60 border border-emerald-500/20 mb-5"
                    data-testid="landing-social-proof-badge"
                  >
                    <div className="flex -space-x-2">
                      {[0, 1, 2].map(i => (
                        <div
                          key={i}
                          className="w-6 h-6 rounded-full border-2 border-zinc-900 bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-[9px] font-bold text-black"
                        >
                          {['D', 'M', 'A'][i]}
                        </div>
                      ))}
                    </div>
                    <span className="text-xs text-zinc-300">
                      <strong className="text-emerald-300 tabular-nums">{publicStats.registered_traders.toLocaleString('de-DE')}+</strong>{' '}
                      Trader sind bereits vorgemerkt
                    </span>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  <Link to="/register">
                    <Button
                      data-testid="kb-teaser-register-btn"
                      className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold group"
                    >
                      Jetzt Platz sichern & benachrichtigt werden
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                  </Link>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Lock className="h-3 w-3" />
                    <span>Exklusiv für Premium ab Launch · Pre-Launch-Vorschau für alle</span>
                  </div>
                </div>
              </div>

              {/* Right: Blurred preview mockup */}
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden border border-emerald-500/25 bg-gradient-to-br from-zinc-900 via-zinc-950 to-zinc-900 shadow-[0_20px_80px_-10px_rgba(16,185,129,0.25)]">
                  {/* Fake app header */}
                  <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center gap-2 bg-zinc-950/80">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                      <div className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                      <div className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                    </div>
                    <div className="flex items-center gap-1.5 ml-2">
                      <GraduationCap className="h-3.5 w-3.5 text-emerald-400" />
                      <span className="text-[11px] font-semibold text-zinc-300">Wissensdatenbank</span>
                      <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-[9px] font-bold text-emerald-300 uppercase tracking-wider">Vorschau</span>
                    </div>
                  </div>
                  {/* Fake prelaunch banner */}
                  <div className="bg-gradient-to-r from-emerald-500/15 via-emerald-500/5 to-transparent border-b border-emerald-500/20 px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[10px]">
                      <Lock className="h-3 w-3 text-emerald-400" />
                      <span className="text-zinc-300"><strong className="text-emerald-300">Launch 01.05.2026</strong> · Voller Zugang inkl. KI-Mentor & Quiz</span>
                    </div>
                    <span className="text-[9px] font-semibold text-emerald-300 tabular-nums">noch 11 Tage</span>
                  </div>
                  {/* Fake content */}
                  <div className="p-5">
                    <p className="text-[10px] text-emerald-400 font-medium mb-1">ICT-Konzepte / ICT-Formationen</p>
                    <h4 className="text-lg font-bold text-white mb-3">Fair Value Gaps</h4>
                    <p className="text-xs text-zinc-300 leading-relaxed mb-2">
                      Ein Fair Value Gap (FVG) ist eine Ungleichgewichtszone im Kursverlauf, die durch
                      schnelle Preisbewegung entsteht und später oft als Magnet für den Preis dient…
                    </p>
                    <div className="blur-[5px] opacity-50 space-y-2 mb-4">
                      <div className="h-3 bg-zinc-800/80 rounded w-full" />
                      <div className="h-3 bg-zinc-800/80 rounded w-11/12" />
                      <div className="h-3 bg-zinc-800/80 rounded w-10/12" />
                      <div className="h-3 bg-zinc-800/80 rounded w-9/12" />
                      <div className="h-3 bg-zinc-800/80 rounded w-11/12" />
                    </div>
                    <div className="px-4 py-3 rounded-xl bg-zinc-950 border border-emerald-500/30 text-center">
                      <div className="flex items-center justify-center gap-1.5 mb-1">
                        <Lock className="h-3 w-3 text-emerald-400" />
                        <span className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold">Vorschau</span>
                      </div>
                      <p className="text-xs font-semibold text-zinc-100">
                        Vollständiger Inhalt ab <span className="text-emerald-300">01.05.2026</span>
                      </p>
                    </div>
                  </div>
                </div>
                {/* Floating badge */}
                <div className="hidden sm:block absolute -bottom-4 -left-4 px-3 py-2 rounded-xl bg-zinc-950 border border-emerald-500/30 shadow-xl">
                  <div className="flex items-center gap-2">
                    <Flame className="h-4 w-4 text-orange-400" />
                    <div>
                      <p className="text-[9px] uppercase tracking-wider text-zinc-500">Lernstreak</p>
                      <p className="text-sm font-bold text-orange-300 leading-none">7 Tage</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* FAQ Section - SEO optimized for Google Featured Snippets */}
      <div id="faq">
      <AnimatedSection className="py-20 bg-zinc-950/50">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeInUp}
          >
            <span className="text-sm font-medium text-primary mb-2 block">Häufige Fragen</span>
            <h2 className="text-3xl md:text-4xl font-display">
              Was Trader wissen wollen
            </h2>
          </motion.div>
          
          <motion.div 
            className="max-w-3xl mx-auto space-y-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {/* FAQ Item 1 */}
            <motion.div variants={fadeInUp} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-3">Was ist ein Trading Journal?</h2>
              <p className="text-muted-foreground leading-relaxed">
                Ein Trading Journal ist ein detailliertes Protokoll aller Handelsaktivitäten. Trader dokumentieren darin Einstiegs- und Ausstiegskurse, Positionsgrößen, Strategien und Emotionen. Diese systematische Aufzeichnung hilft dabei, Muster zu erkennen, Fehler zu analysieren und die Trading-Performance langfristig zu verbessern.
              </p>
            </motion.div>

            {/* FAQ Item 2 */}
            <motion.div variants={fadeInUp} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-3">Warum sollte ich ein Trading Tagebuch führen?</h2>
              <p className="text-muted-foreground leading-relaxed">
                Ein Trading Tagebuch fördert Disziplin und Selbstreflexion. Du erkennst wiederkehrende Fehler, identifizierst profitable Strategien und verstehst, wie Emotionen deine Entscheidungen beeinflussen. Professionelle Trader nutzen Journals, um ihre Stärken auszubauen und Schwächen systematisch zu eliminieren.
              </p>
            </motion.div>

            {/* FAQ Item 3 */}
            <motion.div variants={fadeInUp} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-3">Was sollte in einem Trading Journal stehen?</h2>
              <p className="text-muted-foreground leading-relaxed">
                Ein vollständiges Trading Journal enthält Datum, Symbol, Einstiegs- und Ausstiegskurs, Positionsgröße sowie Gewinn oder Verlust. Zusätzlich dokumentierst du deine Strategie, Marktanalyse, emotionale Verfassung und Learnings. Screenshots der Charts helfen bei der späteren Analyse deiner Entscheidungen.
              </p>
            </motion.div>

            {/* FAQ Item 4 */}
            <motion.div variants={fadeInUp} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-3">Wie berechne ich meine Trading Performance?</h2>
              <p className="text-muted-foreground leading-relaxed">
                Die Trading Performance berechnest du anhand wichtiger Kennzahlen: Gewinnrate (Prozentsatz profitabler Trades), Profit-Faktor (Verhältnis Gewinne zu Verlusten), und Maximum Drawdown (größter Kapitalrückgang). TradeJourn.ai berechnet diese Metriken automatisch und zeigt deinen Fortschritt in übersichtlichen Diagrammen.
              </p>
            </motion.div>

            {/* FAQ Item 5 */}
            <motion.div variants={fadeInUp} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-3">Kann KI mein Trading verbessern?</h2>
              <p className="text-muted-foreground leading-relaxed">
                Ja, KI analysiert große Datenmengen und erkennt Muster, die Menschen übersehen. Der KI-Mentor von TradeJourn.ai bewertet deine Trades objektiv, identifiziert Schwachstellen und gibt konkrete Verbesserungsvorschläge. So lernst du schneller aus Fehlern und entwickelst konsistentere Trading-Gewohnheiten.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </AnimatedSection>
      </div>

      {/* Final CTA */}
      <AnimatedSection className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-950/10 to-transparent" />
        <motion.div 
          className="container mx-auto px-4 relative z-10 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          <motion.div variants={scaleIn}>
            <Brain className="h-16 w-16 text-primary mx-auto mb-6" />
          </motion.div>
          <motion.h2 
            className="text-3xl md:text-4xl font-display mb-4"
            variants={fadeInUp}
          >
            Bereit für KI-gestütztes Trading?
          </motion.h2>
          <motion.p 
            className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto"
            variants={fadeInUp}
          >
            Schließe dich Tradern an, die ihre Performance mit dem KI Mentor verbessern.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/register">
              <Button size="lg" className="text-lg px-10 group" data-testid="final-cta-btn">
                Jetzt starten
                <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </AnimatedSection>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-primary" />
              <span className="font-heading font-bold">TradeJourn<span className="text-primary">.ai</span></span>
            </div>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link to="/impressum" className="hover:text-primary transition-colors">
                Impressum
              </Link>
              <Link to="/agb" className="hover:text-primary transition-colors">
                AGB
              </Link>
              <a href="mailto:support@tradejourn.ai" className="hover:text-primary transition-colors">
                Support
              </a>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2026 TradeJourn.ai. Alle Rechte vorbehalten.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
